import React, { useEffect, useState } from "react";
import { DataGrid, GridToolbar, GridRenderCellParams } from "@mui/x-data-grid";
import {
  createStudent,
  getAllStudents,
  getKidsBysearch,
  deleteStudentProfile,
  toggleUserStatus,
} from "../../API/kids-mgmt";
import { ERROR_TOAST, INFO_TOAST, SUCCESS_TOAST } from "../../helpers/toasters";
import Chip, { ChipProps } from "@mui/material/Chip";
import AddNewKids from "../../components/addNewKids/addNewKids";
import { Add, EditRoadRounded } from "@mui/icons-material";
import EditRoundedIcon from "@mui/icons-material/EditRounded";
import DeleteRoundedIcon from "@mui/icons-material/DeleteRounded";
import ToggleOffIcon from '@mui/icons-material/ToggleOff';
import ToggleOnIcon from '@mui/icons-material/ToggleOn';
import MainTable from "../../components/Datagrid/dataGridTable";
import { calculateAge } from "../../helpers/utils";
import { useSelector } from "react-redux";

export default function KidsList(props) {
  const initialUpdatingRow = {
    uniqueid: 0,
    kidsname: "",
    gender: "",
    age: 0,
    mobnumber: 0,
    city: "",
    country: "",
    courses: [] as string[],
    parents: "",
    _v: 0,
    _id: "",
    isActive: true,
  };

  const [pageSize, setPageSize] = useState(5);
  const [rows, setRows] = useState<any[]>([]);
  const [loading, setLoading] = useState<boolean>(true);
  const [updateKidsProfile, setUpdateKidsProfile] = useState(false);
  const [updatingRow, setUpdatingRow] = useState<any>(initialUpdatingRow);
  const [searchRows, setSearchRows] = useState<String[]>([]);
  const user = useSelector((state: any) => state.auth);

  const fetchStudents = async () => {
    try {
      const data: any[] = await getAllStudents();
      if (data) {
        const updatedData = data.map((user) => {
          return {
            ...user,
            age: calculateAge(user.dob),
            isActive: !!user.isActive
          };
        });
        setRows(updatedData);
        setLoading(false);
      } else {
        ERROR_TOAST("Server error please reload the page !");
      }
    } catch (error) {
      ERROR_TOAST("Failed to fetch students ❌");
      setLoading(false);
    }
  };

  const handleDelete = async (row: any) => {
    if (window.confirm("Are you sure you want to delete this Student Profile?")) {
      try {
        await deleteStudentProfile(row._id);
        SUCCESS_TOAST("Student Profile deleted successfully! ✅");
        fetchStudents();
      } catch (error) {
        ERROR_TOAST("Failed to delete student ❌");
      }
    }
  };

  const handleToggleStatus = async (row: any) => {
    try {
      const result = await toggleUserStatus(row._id);
      if (result && result.isActive !== undefined) {
        setRows((prevRows) =>
          prevRows.map((user) =>
            user._id === row._id ? { ...user, isActive: result.isActive } : user
          )
        );
        SUCCESS_TOAST(`User ${result.isActive ? 'activated' : 'deactivated'} successfully!`);
      } else {
        ERROR_TOAST("Invalid response from server!");
      }
    } catch (error) {
      ERROR_TOAST("Failed to update user status ❌");
    }
  };

  React.useEffect(() => {
    if (!props.search) {
      setSearchRows(rows);
    } else {
      const filteredRows = rows.filter((row: any) =>
        (row.name ?? "").toLowerCase().includes(props.search.toLowerCase()) ||
        (row.contact ?? "").toString().includes(props.search) ||
        ((row.course?.[0]?.name ?? "").toLowerCase().includes(props.search.toLowerCase())) ||
        (row.dob ?? "").toString().includes(props.search) ||
        (row.gender ?? "").toLowerCase().includes(props.search.toLowerCase()) ||
        (row.city ?? "").toLowerCase().includes(props.search.toLowerCase()) ||
        (row.parents_name ?? "").toLowerCase().includes(props.search.toLowerCase()) ||
        (row.gender ?? "").toLowerCase().includes(props.search.toLowerCase()) ||
        (row.state ?? "").toLowerCase().includes(props.search.toLowerCase())
      );
      setSearchRows(filteredRows);
    }
  }, [props.search, rows]);

  useEffect(() => {
    fetchStudents();
  }, []);

  // Base columns without Edit and Delete
  const baseColumns = [
    {
      field: "_id",
      hide: true,
    },
    {
      field: "roll_no",
      headerName: "Roll Number",
      width: 150,
      editable: false,
    },
    {
      field: "name",
      headerName: "Kids Name",
      width: 200,
      editable: false,
    },
    {
      field: "gender",
      headerName: "Gender",
      width: 170,
      editable: false,
    },
    {
      field: "age",
      headerName: "Age",
      width: 100,
      editable: false,
    },
    {
      field: "contact",
      headerName: "Mobile Number",
      width: 150,
      editable: false,
    },
    {
      field: "city",
      headerName: "City",
      width: 150,
      editable: false,
    },
    {
      field: "parents_name",
      headerName: "Parents",
      width: 150,
      editable: false,
    },
    {
      field: "isActive",
      headerName: "Status",
      width: 150,
      renderCell: (cellValues: any) => {
        const isActive = !!cellValues.row.isActive;
        return (
          <div
            style={{
              display: 'flex',
              alignItems: 'center',
              color: isActive ? 'green' : 'red',
              cursor: 'pointer',
              gap: '8px'
            }}
            onClick={() => handleToggleStatus(cellValues.row)}
          >
            {isActive ? <ToggleOnIcon style={{ color: 'green' }} /> : <ToggleOffIcon style={{ color: 'red' }} />}
            {isActive ? 'Active' : 'Inactive'}
          </div>
        );
      },

    },
    {
      field: "Edit",
      width: 80,
      renderCell: (cellValues: any) => {
        return (
          <>
            <EditRoundedIcon
              data-testid="EditRoundedIcon"
              style={{ color: "#8E8EA1", cursor: "pointer" }}
              onClick={(event) => {
                handleCellClick(event, cellValues);
              }}
            />
          </>
        );
      },
    },
  ];

  // Edit and Delete columns to be added conditionally
  const actionColumns = [

    {
      field: "Delete",
      width: 80,
      renderCell: (cellValues: any) => {
        return (
          <>
            <DeleteRoundedIcon
              data-testid="DeleteRoundedIcon"
              style={{ color: "#8E8EA1", cursor: "pointer" }}
              onClick={() => handleDelete(cellValues.row)}
            />
          </>
        );
      },
    },
  ];

  // Combine columns based on user.isSuper
  const columns = user.isSuper ? [...baseColumns, ...actionColumns] : baseColumns;

  const handleCellClick = (
    e: React.MouseEvent,
    value: GridRenderCellParams
  ) => {
    const { row } = value;
    setUpdateKidsProfile(true);
    setUpdatingRow(row);
  };

  const handelClose = () => {
    setUpdateKidsProfile(false);
    setUpdatingRow(initialUpdatingRow);
    fetchStudents();
  };

  return (
    <>
      {searchRows.length === 0 && props.search ? (
        <p className="no-results">🚫 No results found</p>
      ) : (
        <MainTable
          rows={searchRows.length > 0 ? searchRows : rows}
          columns={columns}
          loading={loading}
        />
      )}
      {updateKidsProfile && <AddNewKids handelClose={handelClose} data={updatingRow} />}
    </>
  );
}